import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Brain, Target, Plus, List, Network, GitBranch, User, File, ClipboardList } from 'lucide-react';
import { apiService } from '../services/api';
import { useGoals } from '../hooks/useGoals';
import { Project } from '../types';
import GoalTree from '../components/GoalTree';
import { GoalGraph } from '../components/GoalGraph';
import AIDecomposer from '../components/AIDecomposer';
import { CreateGoalModal } from '../components/Goals/CreateGoalModal';
import { DependencyVisualization } from '../components/Dependencies/DependencyVisualization';
import ExpertProfileModal from '../components/Goals/ExpertProfileModal';
import { ProjectArtifactsList } from '../components/ProjectArtifacts';
import { PlanningStepsList } from '../components/PlanningSteps';

const ProjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAIDecomposer, setShowAIDecomposer] = useState(false);
  const [showCreateGoal, setShowCreateGoal] = useState(false);
  const [showExpertModal, setShowExpertModal] = useState(false);
  const [viewMode, setViewMode] = useState<'tree' | 'graph' | 'dependencies' | 'artifacts' | 'planning'>('tree');
  const { goals, loading: goalsLoading, refreshGoals } = useGoals(id || null);

  useEffect(() => {
    const fetchProject = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const response = await apiService.getProject(id);
        setProject(response.project);
      } catch (err) {
        console.error('Failed to fetch project:', err);
        setError('Failed to load project');
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading project...</div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Project not found</h2>
          <p className="text-gray-600 mb-4">{error || 'The project you are looking for does not exist.'}</p>
          <Link to="/" className="btn-primary">
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  const getHealthColor = (health: string) => {
    switch (health) {
      case 'green': return 'bg-green-100 text-green-800';
      case 'yellow': return 'bg-yellow-100 text-yellow-800';
      case 'red': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2 text-gray-500 hover:text-gray-700">
                <ArrowLeft className="w-4 h-4" />
                Back to Dashboard
              </Link>
              <div className="border-l border-gray-300 h-6" />
              <h1 className="text-xl font-semibold text-gray-900">{project.name}</h1>
              {project.aiGenerated && (
                <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                  AI Generated
                </span>
              )}
            </div>

            <div className="flex items-center gap-3">
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${getHealthColor(project.health)}`}>
                Health: {project.health}
              </span>
              <button
                onClick={() => setShowExpertModal(true)}
                className="btn-secondary flex items-center gap-2"
                title="Configure project expert profile"
              >
                <User className="w-4 h-4" />
                Expert Profile
              </button>
              <button
                onClick={() => setShowAIDecomposer(true)}
                className="btn-primary flex items-center gap-2"
              >
                <Brain className="w-4 h-4" />
                AI Decompose
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-8 max-w-none">
          {/* Project Info */}
          <div className="xl:col-span-1">
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Project Details</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <p className="text-gray-900">{project.description}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <p className="text-gray-900 capitalize">{project.projectType}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Progress</label>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${Math.min(project.progress, 100)}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {Math.round(project.progress)}%
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Goals</label>
                  <p className="text-gray-900">
                    {project.completedGoals} of {project.totalGoals} completed
                  </p>
                </div>

                {project.targetEndDate && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Target Date</label>
                    <p className="text-gray-900">
                      {new Date(project.targetEndDate).toLocaleDateString()}
                    </p>
                  </div>
                )}

                {project.techStack && project.techStack.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tech Stack</label>
                    <div className="flex flex-wrap gap-2">
                      {project.techStack.map((tech, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Goals Section */}
          <div className="xl:col-span-3">
            <div className="card">
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-4">
                  <h3 className="text-lg font-semibold text-gray-900">Project Goals</h3>
                  
                  {/* View Mode Tabs */}
                  <div className="flex items-center bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => setViewMode('tree')}
                      className={`flex items-center gap-2 px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                        viewMode === 'tree'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <List className="w-4 h-4" />
                      Tree
                    </button>
                    <button
                      onClick={() => setViewMode('graph')}
                      className={`flex items-center gap-2 px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                        viewMode === 'graph'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <Network className="w-4 h-4" />
                      Graph
                    </button>
                    <button
                      onClick={() => setViewMode('dependencies')}
                      className={`flex items-center gap-2 px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                        viewMode === 'dependencies'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <GitBranch className="w-4 h-4" />
                      Dependencies
                    </button>
                    <button
                      onClick={() => setViewMode('artifacts')}
                      className={`flex items-center gap-2 px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                        viewMode === 'artifacts'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <File className="w-4 h-4" />
                      Artefakte
                    </button>
                    <button
                      onClick={() => setViewMode('planning')}
                      className={`flex items-center gap-2 px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                        viewMode === 'planning'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <ClipboardList className="w-4 h-4" />
                      Planung
                    </button>
                  </div>
                </div>
                
                <button 
                  onClick={() => setShowCreateGoal(true)}
                  className="btn-secondary flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Goal
                </button>
              </div>

              {goalsLoading ? (
                <div className="text-center py-8">
                  <div className="text-gray-500">Loading goals...</div>
                </div>
              ) : goals.length === 0 && !['dependencies', 'artifacts', 'planning'].includes(viewMode) ? (
                <div className="text-center py-12">
                  <Target className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <h4 className="text-lg font-medium text-gray-900 mb-2">No goals yet</h4>
                  <p className="text-gray-500 mb-6">
                    Start by decomposing your project with AI or create goals manually
                  </p>
                  <div className="flex gap-3 justify-center">
                    <button
                      onClick={() => setShowAIDecomposer(true)}
                      className="btn-primary flex items-center gap-2"
                    >
                      <Brain className="w-4 h-4" />
                      AI Decompose
                    </button>
                    <button 
                      onClick={() => setShowCreateGoal(true)}
                      className="btn-secondary flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Manual Goal
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {viewMode === 'tree' && (
                    <GoalTree
                      goals={goals}
                      onGoalClick={(goalId) => console.log('Goal clicked:', goalId)}
                      onGoalUpdated={refreshGoals}
                    />
                  )}
                  {viewMode === 'graph' && (
                    <GoalGraph
                      goals={goals}
                      onGoalClick={(goalId) => console.log('Goal clicked:', goalId)}
                    />
                  )}
                  {viewMode === 'dependencies' && (
                    <DependencyVisualization projectId={project.id} />
                  )}
                  {viewMode === 'artifacts' && (
                    <ProjectArtifactsList projectId={project.id} />
                  )}
                  {viewMode === 'planning' && (
                    <PlanningStepsList projectId={project.id} />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* AI Decomposer Modal */}
      {showAIDecomposer && (
        <AIDecomposer
          projectId={project.id}
          onClose={() => setShowAIDecomposer(false)}
          onComplete={() => {
            setShowAIDecomposer(false);
            refreshGoals();
          }}
        />
      )}

      {/* Create Goal Modal */}
      {showCreateGoal && (
        <CreateGoalModal
          projectId={project.id}
          onClose={() => setShowCreateGoal(false)}
          onSuccess={() => {
            setShowCreateGoal(false);
            refreshGoals();
          }}
        />
      )}

      {/* Expert Profile Modal */}
      {showExpertModal && (
        <ExpertProfileModal
          isOpen={showExpertModal}
          onClose={() => setShowExpertModal(false)}
          type="project"
          entityId={project.id}
          entityName={project.name}
          onSave={(expertProfile) => {
            setShowExpertModal(false);
            // Could trigger a project refresh or show success message
          }}
        />
      )}
    </div>
  );
};

export default ProjectDetail;